import type { Sheet, SheetData } from "write-excel-file/browser";

import type {
  MarginPlanInputs,
  MarginProjectionYear,
  SimulationResult,
} from "./marginProjection";
import { sortableExportFilename } from "./exportFilename";

export const MARGIN_PLAN_EXPORT_SCHEMA_VERSION = 7;
export const MARGIN_PLAN_INPUT_SCHEMA_VERSION = 12;

export interface FundingSummaryExport {
  finalPortfolio: number;
  finalMargin: number;
  finalNetWorth: number;
  totalInterest: number;
  totalAssetSales: number;
  totalDividendsForSpending: number;
  totalDividendTaxes: number;
  totalRealizedCapitalGains: number;
  totalCapitalGainsTaxes: number;
  totalUnfundedSpending: number;
  minimumMaintenanceCushion: number;
}

export interface MarginPlanExport {
  metadata: {
    exportSchemaVersion: number;
    plannerInputSchemaVersion: number;
    generatedAt: string;
    applicationVersion: string;
    scenarioName: string;
    currency: "USD";
    privacy: string;
  };
  assumptions: MarginPlanInputs;
  deterministicProjection: MarginProjectionYear[];
  simulation: SimulationResult | null;
  fundingComparison: {
    marginFunded: FundingSummaryExport;
    sellAssets: FundingSummaryExport;
  };
  methodology: {
    purpose: string;
    deterministicProjection: string;
    simulation: string;
    importantLimitations: string[];
    suggestedReview: string[];
  };
}

export interface BuildMarginPlanExportOptions {
  scenarioName?: string;
  inputs: MarginPlanInputs;
  projection: MarginProjectionYear[];
  simulation: SimulationResult | null;
  marginFunding: FundingSummaryExport;
  sellFunding: FundingSummaryExport;
  generatedAt?: string;
  applicationVersion?: string;
}

const assumptions: Array<{
  key: keyof MarginPlanInputs;
  label: string;
  unit: string;
  description: string;
}> = [
  { key: "currentAge", label: "Current age", unit: "years", description: "Age at the start of the plan." },
  { key: "retirementAge", label: "Retirement age", unit: "years", description: "Age when retirement spending begins." },
  { key: "retirementAccessAge", label: "Retirement account access age", unit: "years", description: "Age when retirement-account dividends and principal may fund spending." },
  { key: "endAge", label: "Plan end age", unit: "years", description: "Final age in the projection." },
  { key: "startingPortfolio", label: "Starting taxable portfolio", unit: "USD", description: "Marginable taxable portfolio at the start of the plan." },
  { key: "retirementAssets", label: "Starting retirement accounts", unit: "USD", description: "Retirement assets tracked separately until the access age." },
  { key: "externalAssets", label: "Starting external assets", unit: "USD", description: "Non-leverageable assets included in net worth but unavailable for spending or margin collateral." },
  { key: "externalAssetGrowthPercent", label: "External asset annual growth", unit: "%", description: "Nominal annual growth rate for external assets." },
  { key: "initialMargin", label: "Starting margin debt", unit: "USD", description: "Margin loan balance at the start of the plan." },
  { key: "annualSpending", label: "Annual retirement spending", unit: "USD", description: "Fixed spending amount in the selected dollar basis." },
  { key: "spendingDollarBasis", label: "Fixed spending dollar basis", unit: "mode", description: "Whether fixed spending is entered in today's dollars or first-retirement-year dollars." },
  { key: "spendingMode", label: "Spending mode", unit: "choice", description: "Fixed-dollar or portfolio-percentage retirement spending." },
  { key: "spendingPercent", label: "Portfolio spending rate", unit: "%", description: "Annual portfolio percentage spent when percentage spending is selected." },
  { key: "spendingBorrowPercent", label: "Spending funded by borrowing", unit: "%", description: "Share of the remaining spending need intended to be borrowed, subject to guardrails." },
  { key: "dividendYieldPercent", label: "Dividend yield", unit: "%", description: "Annual portfolio dividend yield modeled as cash flow." },
  { key: "dividendTaxRatePercent", label: "Effective taxable dividend tax", unit: "%", description: "Effective tax applied to taxable-account dividends only." },
  { key: "capitalGainsTaxEnabled", label: "Capital-gains tax model", unit: "boolean", description: "Whether taxable asset sales realize gains and reserve estimated tax." },
  { key: "startingTaxableCostBasis", label: "Starting taxable cost basis", unit: "USD", description: "Aggregate adjusted basis used to estimate the gain portion of taxable sales." },
  { key: "capitalGainsTaxRatePercent", label: "Effective capital-gains tax", unit: "%", description: "Effective tax applied to estimated realized gains; may include federal, state, and NIIT assumptions." },
  { key: "maintenanceShockEnabled", label: "Broker maintenance shock", unit: "boolean", description: "Whether a lower collateral limit begins at the configured shock age." },
  { key: "maintenanceShockTimingMode", label: "Broker shock timing", unit: "mode", description: "Whether simulations use a fixed start age or sample one from the configured age range." },
  { key: "maintenanceShockAge", label: "Broker shock age", unit: "age", description: "Fixed start age, or earliest sampled start age, for the broker shock." },
  { key: "maintenanceShockLatestAge", label: "Latest broker shock age", unit: "age", description: "Latest possible shock start age when random-range timing is selected." },
  { key: "maintenanceShockDurationYears", label: "Broker shock duration", unit: "years", description: "Years the stressed limit remains active; zero means through the end of the plan." },
  { key: "stressedMaxLtvPercent", label: "Stressed maximum LTV", unit: "%", description: "Maximum LTV after the broker maintenance shock; equal to 100% minus the stressed maintenance requirement." },
  { key: "dividendUse", label: "Dividend use", unit: "choice", description: "Whether accessible dividends fund spending or repay margin debt." },
  { key: "flexibleSpendingEnabled", label: "Flexible spending enabled", unit: "boolean", description: "Whether spending may be reduced after a market drawdown." },
  { key: "downMarketTriggerPercent", label: "Down-market trigger", unit: "%", description: "Drawdown required before flexible spending may activate." },
  { key: "spendingCutWithdrawalRatePercent", label: "Spending-cut withdrawal-rate trigger", unit: "%", description: "Minimum pre-cut net withdrawal rate on investments accessible for spending; both this and the drawdown threshold must be met." },
  { key: "downMarketSpendingReductionPercent", label: "Down-market spending reduction", unit: "%", description: "Annual spending reduction while the guardrail is active." },
  { key: "minimumSpendingPercent", label: "Minimum spending floor", unit: "%", description: "Minimum share of planned spending allowed by the guardrail." },
  { key: "monthlyContribution", label: "Monthly contribution", unit: "USD", description: "Manual monthly contribution when fixed contributions are selected." },
  { key: "contributionMode", label: "Contribution mode", unit: "choice", description: "Fixed-dollar or portfolio-percentage contribution method." },
  { key: "contributionPercent", label: "Annual contribution rate", unit: "%", description: "Annual portfolio percentage contributed when percentage contributions are selected." },
  { key: "contributionEndAge", label: "Contribution end age", unit: "years", description: "Age when manual contributions stop." },
  { key: "contributionStrategy", label: "Contribution strategy", unit: "choice", description: "Whether contributions invest immediately or repay margin debt first." },
  { key: "cashFlowMode", label: "Pre-retirement cash-flow mode", unit: "choice", description: "Manual contributions or modeled income and living expenses." },
  { key: "annualWorkingIncome", label: "Annual after-tax working income", unit: "USD", description: "First-year take-home employment income before retirement." },
  { key: "incomeGrowthPercent", label: "Working income growth", unit: "%", description: "Nominal annual growth in working income." },
  { key: "annualWorkingExpenses", label: "Annual working expenses", unit: "USD", description: "First-year living expenses before retirement." },
  { key: "workingExpenseInflationPercent", label: "Working expense inflation", unit: "%", description: "Nominal annual increase in pre-retirement expenses." },
  { key: "retirementIncomeMode", label: "Retirement earned-income mode", unit: "choice", description: "Whether coast income is included after retirement." },
  { key: "annualCoastIncome", label: "Annual coast income", unit: "USD", description: "First-year earned income after retirement." },
  { key: "coastIncomeGrowthPercent", label: "Coast income growth", unit: "%", description: "Nominal annual growth in coast income." },
  { key: "coastIncomeEndAge", label: "Coast income end age", unit: "years", description: "Age when coast income stops." },
  { key: "monthlySocialSecurityBenefit", label: "Monthly Social Security benefit", unit: "USD", description: "User-entered monthly benefit estimate in today's plan inputs." },
  { key: "socialSecurityStartAge", label: "Social Security start age", unit: "years", description: "Age when Social Security payments begin." },
  { key: "socialSecurityColaPercent", label: "Social Security COLA", unit: "%", description: "Annual modeled cost-of-living adjustment." },
  { key: "monthlyPensionBenefit", label: "Monthly pension benefit", unit: "USD", description: "Monthly pension payment at its start age." },
  { key: "pensionStartAge", label: "Pension start age", unit: "years", description: "Age when pension payments begin." },
  { key: "pensionColaPercent", label: "Pension COLA", unit: "%", description: "Annual modeled pension adjustment." },
  { key: "expectedReturnPercent", label: "Expected portfolio return", unit: "%", description: "Nominal compound annual growth-rate assumption." },
  { key: "volatilityPercent", label: "Portfolio volatility", unit: "%", description: "Annual volatility used by the Monte Carlo simulation." },
  { key: "marginRatePercent", label: "Fixed margin rate", unit: "%", description: "Annual borrowing rate when fixed-rate mode is selected." },
  { key: "marginRateMode", label: "Margin rate mode", unit: "choice", description: "Fixed rate or simulated rate range." },
  { key: "marginRateMinPercent", label: "Minimum margin rate", unit: "%", description: "Lower bound used in range mode." },
  { key: "marginRateMaxPercent", label: "Maximum margin rate", unit: "%", description: "Upper bound used in range mode." },
  { key: "inflationPercent", label: "Retirement spending inflation", unit: "%", description: "Annual increase in fixed retirement spending." },
  { key: "maxLtvPercent", label: "Maximum loan-to-value", unit: "%", description: "Maintenance limit used to determine simulation success." },
  { key: "pauseBorrowingLtvPercent", label: "Pause-borrowing LTV", unit: "%", description: "LTV at which new spending draws stop." },
  { key: "deleveragingLtvPercent", label: "Deleveraging LTV", unit: "%", description: "LTV at which portfolio sales automatically repay debt." },
  { key: "targetLtvPercent", label: "Target LTV", unit: "%", description: "LTV targeted by an automatic deleveraging sale." },
];

const projectionColumns: Array<{
  key: keyof MarginProjectionYear;
  label: string;
  format?: "currency" | "percent" | "number" | "boolean";
}> = [
  { key: "year", label: "Plan year", format: "number" },
  { key: "age", label: "Ending age", format: "number" },
  { key: "portfolioStart", label: "Starting invested assets", format: "currency" },
  { key: "contribution", label: "Total contributions", format: "currency" },
  { key: "contributionInvested", label: "Contributions invested", format: "currency" },
  { key: "marginRepaidFromContributions", label: "Margin repaid from contributions", format: "currency" },
  { key: "portfolioGrowth", label: "Portfolio growth", format: "currency" },
  { key: "earnedIncome", label: "Total modeled income", format: "currency" },
  { key: "socialSecurityIncome", label: "Social Security income", format: "currency" },
  { key: "pensionIncome", label: "Pension income", format: "currency" },
  { key: "livingExpenses", label: "Living expenses", format: "currency" },
  { key: "marketDrawdownPercent", label: "Starting market drawdown", format: "percent" },
  { key: "plannedWithdrawalRatePercent", label: "Pre-cut net withdrawal rate", format: "percent" },
  { key: "spendingReduction", label: "Flexible spending reduction", format: "currency" },
  { key: "spendingTotal", label: "Net spending need", format: "currency" },
  { key: "spendingFunded", label: "Spending funded", format: "currency" },
  { key: "unfundedSpending", label: "Unfunded spending", format: "currency" },
  { key: "spendingBorrowed", label: "Spending borrowed", format: "currency" },
  { key: "spendingFromPortfolio", label: "Spending from asset sales", format: "currency" },
  { key: "taxableAssetsSold", label: "Gross taxable assets sold", format: "currency" },
  { key: "retirementAssetsSold", label: "Retirement assets sold", format: "currency" },
  { key: "realizedCapitalGains", label: "Estimated realized capital gains", format: "currency" },
  { key: "capitalGainsTaxes", label: "Estimated capital-gains taxes", format: "currency" },
  { key: "dividendIncome", label: "Dividend income", format: "currency" },
  { key: "dividendTaxes", label: "Tax on taxable-account dividends", format: "currency" },
  { key: "accessibleDividendIncome", label: "Accessible dividend income after tax", format: "currency" },
  { key: "inaccessibleRetirementDividendIncome", label: "Inaccessible retirement dividends retained", format: "currency" },
  { key: "dividendUsedForSpending", label: "Dividends used for spending", format: "currency" },
  { key: "marginRepaidFromDividends", label: "Margin repaid from dividends", format: "currency" },
  { key: "dividendReinvested", label: "Accessible dividends reinvested", format: "currency" },
  { key: "marginInterest", label: "Margin interest", format: "currency" },
  { key: "marginRatePercent", label: "Average margin rate", format: "percent" },
  { key: "deleveragingSale", label: "Guardrail asset sales", format: "currency" },
  { key: "portfolioEnd", label: "Ending invested assets", format: "currency" },
  { key: "taxablePortfolioEnd", label: "Ending taxable portfolio", format: "currency" },
  { key: "taxableCostBasisEnd", label: "Ending taxable cost basis", format: "currency" },
  { key: "retirementAssetsEnd", label: "Ending retirement accounts", format: "currency" },
  { key: "externalAssetsEnd", label: "Ending external assets", format: "currency" },
  { key: "marginEnd", label: "Ending margin debt", format: "currency" },
  { key: "ltvPercent", label: "Ending LTV", format: "percent" },
  { key: "netWorth", label: "Ending net worth", format: "currency" },
  { key: "requiredEquity", label: "Required equity", format: "currency" },
  { key: "maintenanceCushion", label: "Maintenance cushion", format: "currency" },
  { key: "effectiveMaxLtvPercent", label: "Effective maximum LTV", format: "percent" },
  { key: "maintenanceBreach", label: "Maintenance breach", format: "boolean" },
];

export function buildMarginPlanExport(options: BuildMarginPlanExportOptions): MarginPlanExport {
  return {
    metadata: {
      exportSchemaVersion: MARGIN_PLAN_EXPORT_SCHEMA_VERSION,
      plannerInputSchemaVersion: MARGIN_PLAN_INPUT_SCHEMA_VERSION,
      generatedAt: options.generatedAt ?? new Date().toISOString(),
      applicationVersion: options.applicationVersion ?? __APP_VERSION__ ?? "development",
      scenarioName: options.scenarioName?.trim() || "Unsaved planning scenario",
      currency: "USD",
      privacy: "No brokerage account identifiers or holdings are included.",
    },
    assumptions: { ...options.inputs },
    deterministicProjection: options.projection.map((row) => ({ ...row })),
    simulation: options.simulation ? structuredClone(options.simulation) : null,
    fundingComparison: {
      marginFunded: { ...options.marginFunding },
      sellAssets: { ...options.sellFunding },
    },
    methodology: {
      purpose: "Planning scenario for review with a financial professional; not investment, tax, or legal advice.",
      deterministicProjection: "Uses the stated nominal compound annual growth rate, converted to an equivalent monthly rate. It is an illustration, not a forecast.",
      simulation: "Monte Carlo paths vary around the stated nominal compound annual growth rate using the stated annual volatility. Combined success requires both fully funded modeled spending and no maintenance-LTV breach. Margin and sell-assets strategies use the same generated paths.",
      importantLimitations: [
        "When enabled, capital-gains tax uses aggregate proportional basis and the configured effective rate. Specific tax lots, holding periods, loss deductions and carryforwards, income-dependent brackets, retirement-withdrawal taxes, early-withdrawal penalties, required minimum distributions, fees, and account-specific rules are not modeled.",
        "Retirement accounts cannot fund spending or repay margin before the configured retirement account access age.",
        "External assets grow and contribute to net worth but cannot fund spending, collateralize margin, or satisfy maintenance requirements.",
        "Social Security and pension values are user estimates, not benefit determinations.",
        "Dividend yield is modeled separately from portfolio growth and may not match an actual portfolio's distributions.",
        "The optional broker-policy stress models one fixed or randomly timed maintenance-limit change for the configured duration; repeated lender changes, asset-specific collateral haircuts, liquidity loss, and execution slippage are not modeled.",
      ],
      suggestedReview: [
        "Validate expected return, volatility, inflation, dividend yield, effective dividend tax, and borrowing-rate assumptions.",
        "Model taxes, account withdrawal ordering, penalties, and required minimum distributions outside this planner.",
        "Confirm Social Security and pension estimates and claiming dates from authoritative statements.",
        "Stress-test lower returns, higher rates and inflation, spending shocks, and reduced credit availability.",
      ],
    },
  };
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadMarginPlanJson(plan: MarginPlanExport): void {
  const blob = new Blob([JSON.stringify(plan, null, 2)], { type: "application/json" });
  downloadBlob(blob, sortableExportFilename(
    plan.metadata.scenarioName,
    "planner-analysis",
    "json",
    plan.metadata.generatedAt,
  ));
}

const header = (value: string) => ({
  value,
  fontWeight: "bold" as const,
  backgroundColor: "#E2E8F0",
});
const currency = (value: number) => ({ value, type: Number, format: "[$$-409]#,##0.00;[Red]-[$$-409]#,##0.00" });
const percent = (value: number) => ({ value: value / 100, type: Number, format: "0.00%" });

function summarySheet(plan: MarginPlanExport): SheetData {
  const final = plan.deterministicProjection.at(-1);
  return [
    [{ value: "Smart Margin planning export", fontWeight: "bold", fontSize: 16, columnSpan: 2 }],
    ["Scenario", plan.metadata.scenarioName],
    ["Generated", new Date(plan.metadata.generatedAt)],
    ["Application version", plan.metadata.applicationVersion],
    ["Planner input schema", plan.metadata.plannerInputSchemaVersion],
    ["Currency", plan.metadata.currency],
    ["Purpose", plan.methodology.purpose],
    [],
    [header("Deterministic result"), header("Value")],
    ["Ending invested assets", currency(final?.portfolioEnd ?? 0)],
    ["Ending external assets", currency(final?.externalAssetsEnd ?? 0)],
    ["Ending margin debt", currency(final?.marginEnd ?? 0)],
    ["Ending net worth", currency(final?.netWorth ?? 0)],
    ["Ending LTV", percent(final?.ltvPercent ?? 0)],
    ["Any maintenance breach", plan.deterministicProjection.some((row) => row.maintenanceBreach)],
    [],
    [header("Monte Carlo result"), header("Value")],
    ["Simulation available", plan.simulation !== null],
    ["Combined success rate", plan.simulation ? percent(plan.simulation.successPercent) : "Pending at export time"],
    ["Collateral survival rate", plan.simulation ? percent(plan.simulation.collateralSuccessPercent) : ""],
    ["Spending success rate", plan.simulation ? percent(plan.simulation.spendingSuccessPercent) : ""],
    ["Paths", plan.simulation?.simulations ?? ""],
    ["Terminal net worth — 10th percentile", plan.simulation ? currency(plan.simulation.terminalNetWorth.p10) : ""],
    ["Terminal net worth — median", plan.simulation ? currency(plan.simulation.terminalNetWorth.p50) : ""],
    ["Terminal net worth — 90th percentile", plan.simulation ? currency(plan.simulation.terminalNetWorth.p90) : ""],
    [],
    [header("Privacy"), header("Detail")],
    ["Included data", plan.metadata.privacy],
  ];
}

function assumptionsSheet(plan: MarginPlanExport): SheetData {
  return [
    [header("Field"), header("Value"), header("Unit"), header("Description"), header("Machine name")],
    ...assumptions.map(({ key, label, unit, description }) => {
      const raw = plan.assumptions[key];
      const value = typeof raw === "number"
        ? unit === "USD" ? currency(raw) : unit === "%" ? percent(raw) : raw
        : raw;
      return [label, value, unit, description, key];
    }),
  ];
}

function projectionSheet(plan: MarginPlanExport): SheetData {
  return [
    projectionColumns.map(({ label }) => header(label)),
    ...plan.deterministicProjection.map((row) => projectionColumns.map(({ key, format }) => {
      const value = row[key];
      if (format === "currency") return currency(value as number);
      if (format === "percent") return percent(value as number);
      return value;
    })),
  ];
}

function fundingSheet(plan: MarginPlanExport): SheetData {
  const metrics: Array<[string, keyof FundingSummaryExport]> = [
    ["Ending invested assets", "finalPortfolio"],
    ["Ending margin debt", "finalMargin"],
    ["Ending net worth", "finalNetWorth"],
    ["Total margin interest", "totalInterest"],
    ["Total asset sales", "totalAssetSales"],
    ["Total dividends used for spending", "totalDividendsForSpending"],
    ["Total taxable-account dividend taxes", "totalDividendTaxes"],
    ["Total estimated realized capital gains", "totalRealizedCapitalGains"],
    ["Total estimated capital-gains taxes", "totalCapitalGainsTaxes"],
    ["Total unfunded spending", "totalUnfundedSpending"],
    ["Minimum maintenance cushion", "minimumMaintenanceCushion"],
  ];
  return [
    [header("Metric"), header("Margin-funded scenario"), header("Sell-assets scenario")],
    ...metrics.map(([label, key]) => [
      label,
      currency(plan.fundingComparison.marginFunded[key]),
      currency(plan.fundingComparison.sellAssets[key]),
    ]),
  ];
}

function simulationSheet(plan: MarginPlanExport): SheetData {
  if (!plan.simulation) return [["Simulation was still pending when this export was created."]];
  const simulation = plan.simulation;
  return [
    [header("Metric"), header("Value")],
    ["Combined success rate", percent(simulation.successPercent)],
    ["Collateral survival rate", percent(simulation.collateralSuccessPercent)],
    ["Spending success rate", percent(simulation.spendingSuccessPercent)],
    ["Full budget funded without flexible cuts", percent(simulation.fullSpendingPercent)],
    ["Successful paths", simulation.successfulPaths],
    ["Paths with unmet spending", simulation.unmetSpendingPaths],
    ["Total paths", simulation.simulations],
    ["Paths with automatic deleveraging", simulation.deleveragingPaths],
    ["Paths with flexible spending cuts", simulation.flexibleSpendingPaths],
    ["Terminal net worth — 10th percentile", currency(simulation.terminalNetWorth.p10)],
    ["Terminal net worth — median", currency(simulation.terminalNetWorth.p50)],
    ["Terminal net worth — 90th percentile", currency(simulation.terminalNetWorth.p90)],
    [],
    [header("Paired strategy comparison"), header("Borrow remainder"), header("Sell + dividends")],
    ["Combined success rate", percent(simulation.strategyComparison.margin.combinedSuccessPercent), percent(simulation.strategyComparison.sellAssets.combinedSuccessPercent)],
    ["Collateral survival rate", percent(simulation.strategyComparison.margin.collateralSuccessPercent), percent(simulation.strategyComparison.sellAssets.collateralSuccessPercent)],
    ["Spending success rate", percent(simulation.strategyComparison.margin.spendingSuccessPercent), percent(simulation.strategyComparison.sellAssets.spendingSuccessPercent)],
    ["Median ending net worth", currency(simulation.strategyComparison.margin.medianEndingNetWorth), currency(simulation.strategyComparison.sellAssets.medianEndingNetWorth)],
    ["Median peak debt", currency(simulation.strategyComparison.margin.medianPeakDebt), currency(simulation.strategyComparison.sellAssets.medianPeakDebt)],
    ["Median lifetime interest", currency(simulation.strategyComparison.margin.medianTotalInterest), currency(simulation.strategyComparison.sellAssets.medianTotalInterest)],
    ["Median capital-gains taxes", currency(simulation.strategyComparison.margin.medianCapitalGainsTaxes), currency(simulation.strategyComparison.sellAssets.medianCapitalGainsTaxes)],
    [],
    [header("Collateral failure detail"), header("Value")],
    ["Failed paths", simulation.failureBreakdown.failedPaths],
    ["Failed paths with down markets", simulation.failureBreakdown.downMarketPaths],
    ["Failed paths where debt outpaced portfolio", simulation.failureBreakdown.debtOutpacedPortfolioPaths],
    ["Earliest breach age", simulation.failureBreakdown.earliestBreachAge ?? "None"],
    ["Median breach age", simulation.failureBreakdown.medianBreachAge ?? "None"],
  ];
}

function methodologySheet(plan: MarginPlanExport): SheetData {
  return [
    [header("Topic"), header("Detail")],
    ["Purpose", plan.methodology.purpose],
    ["Deterministic projection", plan.methodology.deterministicProjection],
    ["Monte Carlo simulation", plan.methodology.simulation],
    ...plan.methodology.importantLimitations.map((detail, index) => [`Limitation ${index + 1}`, detail]),
    ...plan.methodology.suggestedReview.map((detail, index) => [`Suggested review ${index + 1}`, detail]),
  ];
}

export async function downloadMarginPlanExcel(plan: MarginPlanExport): Promise<void> {
  const { default: writeExcelFile } = await import("write-excel-file/browser");
  const sheets: Sheet<Blob>[] = [
    { data: summarySheet(plan), sheet: "Summary", columns: [{ width: 36 }, { width: 90 }], dateFormat: "mmm d yyyy hh:mm" },
    { data: assumptionsSheet(plan), sheet: "Assumptions", columns: [{ width: 34 }, { width: 20 }, { width: 12 }, { width: 76 }, { width: 34 }] },
    { data: projectionSheet(plan), sheet: "Annual Projection", columns: projectionColumns.map(() => ({ width: 22 })), orientation: "landscape" },
    { data: fundingSheet(plan), sheet: "Funding Comparison", columns: [{ width: 40 }, { width: 24 }, { width: 24 }] },
    { data: simulationSheet(plan), sheet: "Simulation", columns: [{ width: 48 }, { width: 24 }] },
    { data: methodologySheet(plan), sheet: "Methodology", columns: [{ width: 28 }, { width: 110 }] },
  ];
  await writeExcelFile(sheets).toFile(sortableExportFilename(
    plan.metadata.scenarioName,
    "adviser-workbook",
    "xlsx",
    plan.metadata.generatedAt,
  ));
}
